import React, { ReactNode, useState, useMemo, useEffect } from 'react';
import { Loader2, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTableData } from './useTableData';

export type ColumnType = 'string' | 'number' | 'boolean' | 'datetime';

export interface Column<T> {
    key: string;
    label: string;
    sortable?: boolean;
    columnType?: ColumnType;
    render: (item: T) => ReactNode;
}

type SortDirection = 'asc' | 'desc';

export interface PaginationConfig {
    itemsPerPage?: number;
    showPagination?: boolean;
    // Controlled pagination
    currentPage?: number;
    totalItems?: number;
    onPageChange?: (page: number) => void;
}

export interface SortConfig {
    sortColumn?: string;
    sortDirection?: SortDirection;
    onSortChange?: (column: string, direction: SortDirection) => void;
}

interface TableProps<T> {
    title: string;
    data: T[] | undefined;
    columns: Column<T>[];
    loading?: boolean;
    emptyMessage?: string;
    defaultSortKey: string;
    defaultSortDirection?: SortDirection;
    sortFn?: (a: T, b: T, sortKey: string, direction: SortDirection) => number;
    getRowKey: (item: T) => string | number;
    pagination?: PaginationConfig;
    sort?: SortConfig;
}

const defaultSortFn = <T,>(columns: Column<T>[]) => {
    return (a: T, b: T, sortKey: string, direction: SortDirection): number => {
        const column = columns.find(col => col.key === sortKey);
        const columnType = column?.columnType || 'string';

        let aValue: any = (a as any)[sortKey];
        let bValue: any = (b as any)[sortKey];

        // Handle null/undefined values - always put them at the end
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return direction === 'asc' ? 1 : -1;
        if (bValue == null) return direction === 'asc' ? -1 : 1;

        // Type-specific sorting
        switch (columnType) {
            case 'number':
                aValue = Number(aValue);
                bValue = Number(bValue);
                break;

            case 'boolean':
                aValue = aValue ? 1 : 0;
                bValue = bValue ? 1 : 0;
                break;

            case 'datetime':
                aValue = new Date(aValue).getTime();
                bValue = new Date(bValue).getTime();
                break;

            case 'string':
            default:
                aValue = String(aValue).toLowerCase();
                bValue = String(bValue).toLowerCase();
                break;
        }

        if (aValue < bValue) return direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return direction === 'asc' ? 1 : -1;
        return 0;
    };
};

export function Table<T>({
    title,
    data,
    columns,
    loading = false,
    emptyMessage = 'No data found',
    defaultSortKey,
    defaultSortDirection = 'asc',
    sortFn,
    getRowKey,
    pagination,
    sort,
}: TableProps<T>) {
    const finalSortFn = sortFn || defaultSortFn(columns);

    // Determine if sorting is controlled
    const isControlledSort = sort?.onSortChange !== undefined;
    
    // Use controlled or uncontrolled sorting
    const internalSort = useTableData({
        data,
        defaultSortKey,
        defaultSortDirection,
        sortFn: finalSortFn,
    });
    
    const sortColumn = isControlledSort ? (sort?.sortColumn ?? defaultSortKey) : internalSort.sortColumn;
    const sortDirection = isControlledSort ? (sort?.sortDirection ?? defaultSortDirection) : internalSort.sortDirection;
    
    // Memoize sortedData to prevent unnecessary re-renders
    const sortedData = useMemo(() => {
        return isControlledSort ? (data ?? []) : internalSort.sortedData;
    }, [isControlledSort, data, internalSort.sortedData]);

    const handleSort = (column: string) => {
        if (isControlledSort && sort?.onSortChange) {
            const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
            sort.onSortChange(column, newDirection);
        } else {
            internalSort.handleSort(column);
        }
    };

    // Determine if pagination is controlled
    const isControlledPagination = pagination?.onPageChange !== undefined;
    
    // Pagination state
    const showPagination = pagination?.showPagination ?? true;
    const itemsPerPage = pagination?.itemsPerPage ?? 10;
    const [internalCurrentPage, setInternalCurrentPage] = useState(1);
    
    const currentPage = isControlledPagination ? (pagination?.currentPage ?? 1) : internalCurrentPage;
    const totalItems = isControlledPagination ? (pagination?.totalItems ?? 0) : sortedData.length;

    // Calculate paginated data
    const { paginatedData, totalPages, startIndex, endIndex } = useMemo(() => {
        if (!showPagination) {
            return {
                paginatedData: sortedData,
                totalPages: 1,
                startIndex: 0,
                endIndex: sortedData.length,
            };
        }

        const totalPages = Math.ceil(totalItems / itemsPerPage);
        
        // For controlled pagination, don't slice data (server already did)
        if (isControlledPagination) {
            const startIndex = (currentPage - 1) * itemsPerPage;
            const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
            return { 
                paginatedData: sortedData, 
                totalPages, 
                startIndex, 
                endIndex 
            };
        }
        
        // For uncontrolled pagination, slice the data
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = Math.min(startIndex + itemsPerPage, sortedData.length);
        const paginatedData = sortedData.slice(startIndex, endIndex);

        return { paginatedData, totalPages, startIndex, endIndex };
    }, [sortedData, currentPage, itemsPerPage, showPagination, totalItems, isControlledPagination]);

    // Reset to page 1 when data changes (only for uncontrolled)
    useEffect(() => {
        if (!isControlledPagination) {
            setInternalCurrentPage(1);
        }
    }, [sortedData, isControlledPagination]);

    const handlePageChange = (page: number) => {
        if (isControlledPagination && pagination?.onPageChange) {
            pagination.onPageChange(page);
        } else {
            setInternalCurrentPage(page);
        }
    };

    const handlePrevious = () => {
        handlePageChange(Math.max(1, currentPage - 1));
    };

    const handleNext = () => {
        handlePageChange(Math.min(totalPages, currentPage + 1));
    };

    const SortIcon = ({ column }: { column: string }) => {
        if (sortColumn !== column) {
            return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
        }
        return sortDirection === 'asc' 
            ? <ArrowUp className="h-4 w-4 text-indigo-600" />
            : <ArrowDown className="h-4 w-4 text-indigo-600" />;
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            </div>

            {loading ? (
                <div className="flex justify-center items-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {columns.map((column) => (
                                    <th
                                        key={column.key}
                                        className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                                            column.sortable !== false
                                                ? 'cursor-pointer hover:bg-gray-100 transition-colors'
                                                : ''
                                        }`}
                                        onClick={
                                            column.sortable !== false
                                                ? () => handleSort(column.key)
                                                : undefined
                                        }
                                    >
                                        <div className="flex items-center space-x-1">
                                            <span>{column.label}</span>
                                            {column.sortable !== false && <SortIcon column={column.key} />}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedData.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={columns.length}
                                        className="px-6 py-12 text-center text-gray-500"
                                    >
                                        {emptyMessage}
                                    </td>
                                </tr>
                            ) : (
                                paginatedData.map((item) => (
                                    <tr key={getRowKey(item)} className="hover:bg-gray-50">
                                        {columns.map((column) => (
                                            <td
                                                key={column.key}
                                                className="px-6 py-4 whitespace-nowrap"
                                            >
                                                {column.render(item)}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination Controls */}
            {showPagination && !loading && totalItems > 0 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                        Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                        <span className="font-medium">{endIndex}</span> of{' '}
                        <span className="font-medium">{totalItems}</span> results
                    </div>

                    <div className="flex items-center space-x-2">
                        <button
                            onClick={handlePrevious}
                            disabled={currentPage === 1}
                            className={`p-2 rounded-md ${
                                currentPage === 1
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </button>

                        <div className="flex items-center space-x-1">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                                // Show first page, last page, current page, and pages around current
                                const showPage =
                                    page === 1 ||
                                    page === totalPages ||
                                    (page >= currentPage - 1 && page <= currentPage + 1);

                                const showEllipsis =
                                    (page === currentPage - 2 && currentPage > 3) ||
                                    (page === currentPage + 2 && currentPage < totalPages - 2);

                                if (showEllipsis) {
                                    return (
                                        <span key={page} className="px-2 text-gray-500">
                                            ...
                                        </span>
                                    );
                                }

                                if (!showPage) {
                                    return null;
                                }

                                return (
                                    <button
                                        key={page}
                                        onClick={() => handlePageChange(page)}
                                        className={`px-3 py-1 rounded-md text-sm font-medium ${
                                            currentPage === page
                                                ? 'bg-indigo-600 text-white'
                                                : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            onClick={handleNext}
                            disabled={currentPage === totalPages}
                            className={`p-2 rounded-md ${
                                currentPage === totalPages
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                            <ChevronRight className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
